import React, { useEffect, useRef } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import Svg, { Circle, G } from 'react-native-svg';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import theme from 'shared/styles/theme';

const trackerSize = 56;
const ANIMATION_DURATION = 700;
const trackerStrokeWidth = 6;

interface CircularProgressTrackerProps {
  completedSteps: number;
  totalSteps: number;
}

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const CircularProgressTracker = ({
  completedSteps,
  totalSteps,
}: CircularProgressTrackerProps) => {
  const animatedProgress = useRef(new Animated.Value(0)).current;
  const animationRef = useRef<Animated.CompositeAnimation | null>(null);
  const radius = (trackerSize - trackerStrokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    let newProgressValue = 0;
    if (completedSteps === 0 && totalSteps > 0) {
      newProgressValue = 0.01;
    } else if (totalSteps > 0) {
      newProgressValue = completedSteps / totalSteps;
    }

    if (animationRef.current) {
      animationRef.current.stop();
    }

    animationRef.current = Animated.timing(animatedProgress, {
      toValue: newProgressValue,
      duration: ANIMATION_DURATION,
      useNativeDriver: false,
    });

    animationRef.current.start();

    return (): void => {
      if (animationRef.current) {
        animationRef.current.stop();
        animationRef.current = null;
      }
      animatedProgress.stopAnimation();
    };
  }, [completedSteps, totalSteps, animatedProgress]);

  const strokeDashoffset = animatedProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [circumference, 0],
    extrapolate: 'clamp',
  });

  return (
    <View style={styles.container}>
      <Svg width={trackerSize} height={trackerSize}>
        <G rotation="-90" origin={`${trackerSize / 2}, ${trackerSize / 2}`}>
          <Circle
            cx={trackerSize / 2}
            cy={trackerSize / 2}
            r={radius}
            stroke={theme.color.background.secondary}
            strokeWidth={trackerStrokeWidth}
            fill="transparent"
          />

          <AnimatedCircle
            cx={trackerSize / 2}
            cy={trackerSize / 2}
            r={radius}
            stroke={theme.color.interactive.primary}
            strokeWidth={trackerStrokeWidth}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </G>
      </Svg>

      <View style={styles.textContainer}>
        <BodySmText style={styles.progressText}>
          {`${completedSteps}/${totalSteps}`}
        </BodySmText>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    height: trackerSize,
    justifyContent: 'center',
    width: trackerSize,
  },
  progressText: { color: theme.color.text.primary },
  textContainer: { position: 'absolute' },
});

export default CircularProgressTracker;
